import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "core/components/Button";
import Field from "core/components/Field";
import Flex from "core/components/Flex";
import {
  Panel,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from "core/components/Panel";
import { z } from "zod";
import { minLength, required, email, validate } from "common/validation";
import Typography from "core/components/Typography";
import { useAssignableOrganizationsListQuery } from "core/queries";
import { createUserProfiles } from "modules/admin/actions";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";
import {
  ROLES,
  TABLEAU_ROLES,
  ANALYTICS_HUB_USER_ROLES,
  SYSTEM_ROLE_KEYS,
} from "core/constants";
import useForm from "core/hooks/useForm";
import Error from "core/components/Error";
import { toast } from "sonner";
import { MembershipCard, MembershipCardError } from "./MembershipCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-regular-svg-icons";

const roleOptions = Object.entries(ROLES).map(([key, value]) => {
  return {
    label: value,
    value: key,
  };
});

const ahuRoleOptions = Object.entries(ANALYTICS_HUB_USER_ROLES).map(
  ([key, value]) => {
    return {
      label: value,
      value: key,
    };
  }
);

const biRoleOptions = Object.entries(TABLEAU_ROLES).map(([key, value]) => {
  return {
    label: value,
    value: key,
  };
});

const getAppRoleOptionsByOrgId = (orgId) => {
  if (orgId !== "populi") {
    return roleOptions.filter(
      (option) => !SYSTEM_ROLE_KEYS.includes(option.value)
    );
  }

  return roleOptions;
};

const getBiRoleOptionsByOrgId = (orgId) => {
  if (orgId !== "populi") {
    return biRoleOptions.filter((option) => option.value !== "creator");
  }

  return biRoleOptions;
};

const formSchema = z
  .object({
    name: z.string().min(2),
    email: z.email(),
    alternateEmail: z.union([z.email(), z.literal("")]).nullish(),
    memberships: z.array(
      z.object({
        organization_id: z.string().min(1),
        role: z.string().min(1),
        analytics_hub_access: z
          .enum(Object.keys(ANALYTICS_HUB_USER_ROLES))
          .default("none"),
        tableau_config: z.object({
          license: z.enum(Object.keys(TABLEAU_ROLES)),
          enabled: z.boolean().default(false),
        }),
        direct_data_access_enabled: z.boolean(),
        // status: z.enum(["success", "error", "none"]).default("none"),
        // errorMessage: z.string().nullish(),
      })
    ),
  })
  .transform((data) => {
    // Prepare data for form submission
    const { alternateEmail, ...includedFields } = data;

    return {
      ...includedFields,
      alternate_email: alternateEmail || null,
      memberships: data.memberships.map((m) => {
        return {
          ...m,
          tableau_config: {
            license: m.tableau_config.license,
            enabled: m.tableau_config.license === "none" ? false : true,
          },
        };
      }),
    };
  });

const initialValues = {
  name: "",
  email: "",
  alternateEmail: "",
  memberships: [],
};

const CreateUserPanel = () => {
  const [isOpen, setOpen] = useState(true);
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const { formValues, errors, onFieldChange, onFormReset, isDisabled } =
    useForm({
      initialValues,
      formSchema,
      shouldValidateWithSchema: true,
      disabledUntilTouched: true,
    });

  // Fetch the assignable orgs list based on current user's role & email domain of new user
  const { data: assignableOrgOptions, isFetching: isFetchingAssignableOrgs } =
    useAssignableOrganizationsListQuery({
      email: formValues.email,
      select: (data) => {
        return data.map((assignableOrg) => {
          return {
            label: assignableOrg.organization_name,
            value: assignableOrg.organization_id,
          };
        });
      },
      enabled: z.email().safeParse(formValues.email).success,
    });

  const createUserMutation = useAuthenticatedMutation(createUserProfiles);
  const { mutate: createUser, isPending: isCreatingUser } = useMutation({
    mutationFn: createUserMutation,
    onError: (error, variables, onMutateResult, context) => {
      console.log("createUserMutation.onError", { error });
      if (Array.isArray(error)) {
        setError(error[0]);
      } else if (typeof error === "string") {
        setError(error);
      } else {
        setError("There was an issue creating user.");
      }
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      console.log("createUserMutation.onSuccess", {
        data,
        variables,
        onMutateResult,
        context,
      });

      if (data.code === 207) {
        setError(data.results[0]?.error ?? "There was an issue creating user.");
      } else {
        queryClient.invalidateQueries({ queryKey: ["memberships"] });
        queryClient.invalidateQueries({ queryKey: ["user-profiles"] });
        toast.success("Created user successfully!");
        setOpen(false);
      }
    },
  });

  const handleAddMembership = async () => {
    setError(null);
    if (!formValues.email || !formValues.name) return;

    const newMembership = {
      organization_id: "",
      role: "orgUser",
      analytics_hub_access: "none",
      tableau_config: {
        license: "none",
        enabled: false,
      },
      direct_data_access_enabled: false,
    };

    onFieldChange("memberships")([...formValues.memberships, newMembership]);
  };

  const handleUpdateMembership = (index, updates) => {
    setError(null);

    const updated = [...formValues.memberships];
    updated[index] = {
      ...updated[index],
      ...updates,
    };

    onFieldChange("memberships")(updated);
  };

  const handleRemoveMembership = (index) => {
    setError(null);
    const updated = formValues.memberships.filter((_, i) => i !== index);
    onFieldChange("memberships")(updated);
  };

  const handleCancel = () => {
    setError(null);
    onFormReset();
    setOpen(false);
  };

  const handleCreateUser = () => {
    // Use schema to get the transform
    const newUser = formSchema.safeParse(formValues).data;
    console.log({ newUser });
    createUser({ profiles: [newUser] });
  };

  return (
    <Panel isOpen={isOpen} onOpenChange={setOpen} onCloseRedirectTo="../">
      <PanelHeader>
        <PanelTitle>Create User</PanelTitle>
      </PanelHeader>
      <PanelContent>
        <Error>{error}</Error>
        <Flex direction="column" gap="16px">
          <Flex
            direction="column"
            style={{ borderBottom: "1px solid #898d8f" }}
          >
            <Field
              type="text"
              id="name"
              labelText="Name"
              isRequired={true}
              value={formValues.name}
              error={errors.name}
              onChange={(value, errorMessage) => {
                setError(null);
                onFieldChange("name")(value, errorMessage);
              }}
              validator={validate(required(), minLength(2))}
            />
            <Field
              type="text"
              id="email"
              labelText="Email"
              isRequired={true}
              value={formValues.email}
              error={errors.email}
              onChange={(value, errorMessage) => {
                setError(null);
                onFieldChange("email")(value, errorMessage);
              }}
              validator={validate(required(), email())}
              autoComplete="new-password"
            />
            <Field
              type="text"
              id="alternateEmail"
              labelText="Alternate Email"
              isRequired={false}
              value={formValues.alternateEmail}
              error={errors.alternateEmail}
              onChange={(value, errorMessage) => {
                setError(null);
                onFieldChange("alternateEmail")(value, errorMessage);
              }}
              validator={validate(email())}
              autoComplete="new-password"
            />
          </Flex>

          <Flex direction="column" gap="16px">
            <Flex direction="column" gap="4px">
              <Flex direction="row" gap="4px" justifyContent="space-between">
                <Typography variant="h5" noMargin>
                  Memberships
                </Typography>
                <Button
                  variant="text"
                  onClick={handleAddMembership}
                  disabled={!formValues.name || !formValues.email}
                >
                  Add
                </Button>
              </Flex>
              <Typography variant="body" noMargin>
                Users can only be members of organizations that allow their
                domain access.
              </Typography>
            </Flex>
          </Flex>

          {formValues.memberships?.map((membership, index) => {
            const hasOrgSelected = !!membership.organization_id;
            const takenOrgIds = formValues.memberships
              .map((m) => m.organization_id)
              .filter(Boolean);
            const filteredOrgOptions = assignableOrgOptions.filter((option) => {
              if (
                hasOrgSelected &&
                option.value === membership.organization_id
              ) {
                return true;
              }

              return !takenOrgIds.includes(option.value);
            });

            return (
              <MembershipCard
                key={index}
                status={membership.status}
                dataTest={`membership-card-${
                  membership.organization_id || index
                }`}
              >
                <Flex
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap="8px"
                >
                  <Field
                    type="combobox"
                    size="small"
                    id="organization"
                    placeholder="Select Organization"
                    isRequired
                    isSearchable
                    hideError={true}
                    showSkeleton={
                      isFetchingAssignableOrgs && !membership.organization_id
                    }
                    options={filteredOrgOptions}
                    value={membership.organization_id}
                    onChange={(orgId) => {
                      const selectedOrg = assignableOrgOptions?.find(
                        (option) => option.value === orgId
                      );

                      handleUpdateMembership(index, {
                        organization_id: selectedOrg.value,
                      });
                    }}
                  />
                  <Button
                    variant="icon"
                    size="small"
                    onClick={() => handleRemoveMembership(index)}
                    style={{ marginRight: "4px" }}
                  >
                    <FontAwesomeIcon icon={faClose} />
                  </Button>
                </Flex>

                <Flex direction="row" gap="8px">
                  <Field
                    type="combobox"
                    size="small"
                    id="role"
                    labelText="Role"
                    isRequired
                    isDisabled={
                      isFetchingAssignableOrgs && !membership.organization_id
                    }
                    hideError={true}
                    options={getAppRoleOptionsByOrgId(
                      membership.organization_id
                    )}
                    value={membership.role}
                    onChange={(value) => {
                      handleUpdateMembership(index, {
                        role: value,
                      });
                    }}
                  />

                  <Field
                    type="combobox"
                    size="small"
                    id="analyticsHubUser"
                    labelText="Analytics Hub User"
                    isRequired
                    isDisabled={
                      isFetchingAssignableOrgs && !membership.organization_id
                    }
                    hideError={true}
                    options={ahuRoleOptions}
                    value={membership.analytics_hub_access}
                    onChange={(value) => {
                      handleUpdateMembership(index, {
                        analytics_hub_access: value,
                      });
                    }}
                  />
                </Flex>

                <Flex direction="row" gap="8px">
                  <Field
                    type="combobox"
                    id="biPortalUser"
                    size="small"
                    labelText="BI Portal User"
                    isRequired
                    isDisabled={
                      isFetchingAssignableOrgs && !membership.organization_id
                    }
                    hideError={true}
                    placeholder="Please Select"
                    options={getBiRoleOptionsByOrgId(
                      membership.organization_id
                    )}
                    value={membership.tableau_config?.license}
                    onChange={(value) => {
                      handleUpdateMembership(index, {
                        tableau_config: {
                          ...membership.tableau_config,
                          license: value,
                        },
                      });
                    }}
                  />

                  <Field
                    type="combobox"
                    id="directDataAccess"
                    size="small"
                    labelText="Data Access"
                    isRequired
                    isDisabled={
                      isFetchingAssignableOrgs && !membership.organization_id
                    }
                    hideError={true}
                    options={[
                      { label: "Enabled", value: true },
                      { label: "Disabled", value: false },
                    ]}
                    value={membership.direct_data_access_enabled}
                    onChange={(value) => {
                      handleUpdateMembership(index, {
                        direct_data_access_enabled: value,
                      });
                    }}
                  />
                </Flex>
                {membership.errorMessage && (
                  <MembershipCardError>
                    {membership.errorMessage}
                  </MembershipCardError>
                )}
              </MembershipCard>
            );
          })}

          {/* Action buttons */}
          <Flex direction="row" gap="16px" justifyContent="flex-end">
            <Button variant="outlined" width="auto" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              width="auto"
              onClick={handleCreateUser}
              disabled={isDisabled || isCreatingUser}
              isLoading={isCreatingUser}
            >
              Create User
            </Button>
          </Flex>
        </Flex>
      </PanelContent>
    </Panel>
  );
};

export default CreateUserPanel;
